import { useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { CheckCircle2, Package, MapPin, Truck, ArrowRight, Home, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { useOrderByNumber } from "@/hooks/queries";
import { OrderStatus } from "@/types/enums";
import { Skeleton } from "@/components/ui/skeleton";

function getOrderProgress(status?: OrderStatus): number {
  switch (status) {
    case OrderStatus.Pending:
      return 0;
    case OrderStatus.Confirmed:
      return 1;
    case OrderStatus.Processing:
    case OrderStatus.ReadyForPickup:
      return 2;
    case OrderStatus.OutForDelivery:
      return 3;
    case OrderStatus.Delivered:
      return 4;
    default:
      return 0;
  }
}

function isTerminalFailure(status?: OrderStatus): boolean {
  return (
    status === OrderStatus.Cancelled ||
    status === OrderStatus.Returned ||
    status === OrderStatus.Refunded
  );
}

const OrderConfirmation = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const orderNumber = orderId ?? "";
  const { data: order, isLoading, isError } = useOrderByNumber(orderNumber, orderNumber.length > 0);

  const displayId = order?.orderNumber ?? orderNumber;
  const progress = getOrderProgress(order?.status);
  const failed = isTerminalFailure(order?.status);

  const steps = useMemo(
    () => [
      { icon: CheckCircle2, label: "Order Confirmed", desc: "Your order is confirmed", step: 1 },
      { icon: Package, label: "Preparing", desc: "Store is preparing your order", step: 2 },
      { icon: Truck, label: "Out for Delivery", desc: "Your order is on its way", step: 3 },
      { icon: MapPin, label: "Delivered", desc: "Enjoy your products!", step: 4 },
    ],
    [],
  );

  return (
    <div className="min-h-screen bg-background flex items-center justify-center py-12">
      <div className="container mx-auto px-4 max-w-lg">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="bg-card rounded-2xl border border-border/50 p-8 text-center"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
            className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-5 ${
              failed ? "bg-destructive/10" : "bg-success/10"
            }`}
          >
            {failed ? (
              <XCircle className="w-10 h-10 text-destructive" />
            ) : (
              <CheckCircle2 className="w-10 h-10 text-success" />
            )}
          </motion.div>

          <h1 className="font-heading text-2xl font-bold mb-2">
            {failed ? "Order Update" : "Order Placed! 🎉"}
          </h1>
          <p className="text-muted-foreground text-sm mb-6">
            {failed
              ? `This order is marked as ${order?.statusText?.toLowerCase() ?? "cancelled"}.`
              : "Your order has been placed successfully. You'll receive updates as it progresses."}
          </p>

          <div className="bg-secondary/50 rounded-xl p-4 mb-6">
            <p className="text-xs text-muted-foreground mb-1">Order Number</p>
            {isLoading ? (
              <Skeleton className="h-7 w-40 mx-auto" />
            ) : (
              <p className="font-heading font-bold text-lg text-primary">{displayId}</p>
            )}
            {order && (
              <p className="text-xs text-muted-foreground mt-2">
                {order.storeName} • ₹{order.totalAmount} • {order.paymentMethodText}
                {order.statusText ? ` • ${order.statusText}` : ""}
              </p>
            )}
            {isError && (
              <p className="text-xs text-muted-foreground mt-2">Order reference saved. Track it from your account.</p>
            )}
          </div>

          {!failed && (
            <div className="space-y-4 mb-8 text-left">
              {steps.map((step) => {
                const isComplete = progress >= step.step;
                const isCurrent = progress === step.step || (progress === 0 && step.step === 1);
                return (
                  <div key={step.label} className="flex items-start gap-3">
                    <div
                      className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${
                        isComplete
                          ? isCurrent
                            ? "bg-success text-success-foreground"
                            : "bg-success/20 text-success"
                          : "bg-secondary text-muted-foreground"
                      }`}
                    >
                      <step.icon className="w-4 h-4" />
                    </div>
                    <div>
                      <p className="font-semibold text-sm">{step.label}</p>
                      <p className="text-xs text-muted-foreground">{step.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-3">
            <Link to="/" className="flex-1">
              <Button variant="outline" className="w-full gap-2">
                <Home className="w-4 h-4" /> Back to Home
              </Button>
            </Link>
            <Link to="/account/orders" className="flex-1">
              <Button className="w-full gap-2 bg-gradient-primary hover:opacity-90">
                Track Order <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default OrderConfirmation;
